/* "Pair it up" add-on row (snippets/upsell-gp.liquid). Resolves the chosen
   options to a variant, adds it with /cart/add.js and then:
   - with the cart drawer present, re-renders it through Dawn's own
     CartDrawer.renderContents, which also opens it; the re-rendered drawer
     comes back from Liquid without the row, since the cart now holds it;
   - on /cart, reloads the page, because Dawn's cart page refreshes its items
     but not its totals after an outside add. */
class UpsellGp extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.querySelector('[data-upsell-variants]').textContent);
    } catch (e) {
      return;
    }
    /* "No thanks" holds until the drawer closes (merchant, 11 October 2026):
       the drawer re-renders from Liquid after every change, so a row that
       connects while dismissed starts hidden. On /cart it holds until the
       page reloads. */
    this.hidden = UpsellGp.dismissed;
    const dismiss = this.querySelector('[data-upsell-dismiss]');
    if (dismiss) dismiss.addEventListener('click', () => this.dismiss());

    this.button = this.querySelector('[data-upsell-add]');
    this.labelEl = this.querySelector('[data-upsell-label]');
    this.spinner = this.button.querySelector('.loading__spinner');
    this.errorEl = this.querySelector('[data-upsell-error]');
    this.selects = Array.from(this.querySelectorAll('select[data-option-index]'));

    /* The drawer's row sits inside <cart-drawer-items>, whose own change
       listener treats any change as a quantity edit, so a select's change
       stops here. */
    this.selects.forEach((select) =>
      select.addEventListener('change', (e) => {
        e.stopPropagation();
        this.onChange();
      })
    );
    this.button.addEventListener('click', () => this.add());
    this.onChange();
  }

  onChange() {
    const chosen = this.selects
      .slice()
      .sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex)
      .map((s) => s.value);
    const variant = this.selects.length
      ? this.variants.find((v) => v.options.every((o, i) => o === chosen[i]))
      : this.variants[0];
    this.variant = variant;
    const ok = Boolean(variant && variant.available);
    this.button.disabled = !ok;
    this.labelEl.textContent = ok ? this.dataset.labelAdd : this.dataset.labelSold;
    this.errorEl.hidden = true;
  }

  async add() {
    if (!this.variant || !this.variant.available || this.button.classList.contains('loading')) return;
    this.setLoading(true);
    const drawer = document.querySelector('cart-drawer');
    const body = { items: [{ id: this.variant.id, quantity: 1 }] };
    if (drawer && this.dataset.context !== 'cart') {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || state.message || '');

      if (this.dataset.context === 'cart') {
        window.location.reload();
        return;
      }
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'upsell-gp', productVariantId: this.variant.id });
      }
      if (drawer && state.sections) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(state);
      } else {
        window.location.href = window.routes.cart_url;
      }
    } catch (error) {
      this.errorEl.textContent = error.message || this.dataset.error;
      this.errorEl.hidden = false;
      this.setLoading(false);
    }
  }

  dismiss() {
    UpsellGp.dismissed = true;
    this.hidden = true;

    /* Dawn's CartDrawer.close() removes .active; once it has, the next
       opening offers the row again. */
    const drawer = this.closest('cart-drawer');
    if (!drawer || UpsellGp.closeObserver) return;
    UpsellGp.closeObserver = new MutationObserver(() => {
      if (drawer.classList.contains('active')) return;
      UpsellGp.closeObserver.disconnect();
      UpsellGp.closeObserver = null;
      UpsellGp.dismissed = false;
      drawer.querySelectorAll('upsell-gp').forEach((row) => {
        row.hidden = false;
      });
    });
    UpsellGp.closeObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  setLoading(loading) {
    this.button.classList.toggle('loading', loading);
    if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
    this.button.setAttribute('aria-busy', String(loading));
  }
}

UpsellGp.dismissed = false;
UpsellGp.closeObserver = null;

if (!customElements.get('upsell-gp')) {
  customElements.define('upsell-gp', UpsellGp);
}
